import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ArrowRight, Shield, ArrowDownToLine } from 'lucide-react';
import { trackLead } from '../utils/pixel';
type FormData = {
  name: string;
  email: string;
  interest: string;
  investment: string;
};
export function LeadForm() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    interest: '',
    investment: ''
  });
  const updateData = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleNext = () => {
    setStep(prev => prev + 1);
  };
  const handleStep1Submit = () => {
    trackLead({
      content_name: 'IUL Guide Download',
      name: formData.name,
      email: formData.email
    });
    handleNext();
  };
  const handleInterestSelect = (interest: string) => {
    updateData('interest', interest);
    handleNext();
  };
  const handleInvestmentSelect = (investment: string) => {
    updateData('investment', investment);
    // Simulate form submission
    console.log('Form submitted successfully:', {
      ...formData,
      investment
    });
    handleNext();
  };
  const isStep1Valid = formData.name.trim() !== '' && formData.email.includes('@');
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };
  return <section id="lead-form" className="py-24 bg-white relative" data-id="element-42">
      <div className="max-w-6xl mx-auto px-6" data-id="element-43">
        <div className="flex flex-col lg:flex-row bg-white rounded-3xl overflow-hidden shadow-2xl border border-gray-100" data-id="element-44">
          {/* Left Side - Image & Context */}
          <div className="lg:w-5/12 relative min-h-[300px] lg:min-h-auto" data-id="element-45">
            <div className="absolute inset-0 bg-cover bg-center" style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800")'
          }} data-id="element-46"></div>
            <div className="absolute inset-0 bg-deep-blue/85 mix-blend-multiply" data-id="element-47"></div>
            <div className="absolute inset-0 p-10 flex flex-col justify-between text-white" data-id="element-48">
              <div data-id="element-49">
                <Shield className="w-10 h-10 text-vitality-green mb-6" data-id="element-50" />
              </div>
              <div data-id="element-51">
                <h3 className="text-3xl font-bold mb-4 leading-tight" data-id="element-52">
                  Tu futuro comienza hoy
                </h3>
                <p className="text-gray-300 text-lg leading-relaxed" data-id="element-53">
                  Da el primer paso hacia la tranquilidad financiera de tu
                  familia. Completa este breve formulario para recibir tu guía
                  personalizada.
                </p>
              </div>
            </div>
          </div>

          {/* Right Side - Form Steps */}
          <div className="lg:w-7/12 p-8 md:p-14 flex flex-col justify-center bg-light-bg relative min-h-[500px]" data-id="element-54">
            {/* Progress Indicator */}
            {step < 4 && <div className="mb-10" data-id="element-55">
                <div className="flex gap-2 mb-2" data-id="element-56">
                  {[1, 2, 3].map(i => <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${i <= step ? 'bg-vitality-green' : 'bg-gray-200'}`} data-id="element-57" />)}
                </div>
                <p className="text-sm text-gray-500 font-medium text-right" data-id="element-58">
                  Paso {step} de 3
                </p>
              </div>}

            <div className="relative flex-1" data-id="element-59">
              <AnimatePresence mode="wait" custom={1} data-id="element-60">
                {/* STEP 1: Contact Info */}
                {step === 1 && <motion.div key="step1" custom={1} variants={variants} initial="enter" animate="center" exit="exit" transition={{
                duration: 0.4,
                ease: 'easeInOut'
              }} className="w-full" data-id="element-61">
                    <h3 className="text-2xl font-bold text-deep-blue mb-6" data-id="element-62">
                      ¿A dónde enviamos tu guía?
                    </h3>
                    <div className="space-y-5" data-id="element-63">
                      <div data-id="element-64">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-65">
                          Nombre completo
                        </label>
                        <input type="text" value={formData.name} onChange={e => updateData('name', e.target.value)} placeholder="Ej. Juan Pérez" className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-vitality-green focus:border-vitality-green outline-none transition-all bg-white" data-id="element-66" />
                      </div>
                      <div data-id="element-67">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-68">
                          Correo electrónico
                        </label>
                        <input type="email" value={formData.email} onChange={e => updateData('email', e.target.value)} placeholder="tu@correo.com" className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-vitality-green focus:border-vitality-green outline-none transition-all bg-white" data-id="element-69" />
                      </div>
                      <button onClick={handleStep1Submit} disabled={!isStep1Valid} className={`w-full mt-6 flex items-center justify-center gap-2 py-4 rounded-xl font-semibold text-lg transition-all ${isStep1Valid ? 'bg-vitality-green hover:bg-vitality-green-hover text-white shadow-lg hover:shadow-xl' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-70">
                        Accede a tu guía gratuita
                        <ArrowRight className="w-5 h-5" data-id="element-71" />
                      </button>
                    </div>
                  </motion.div>}

                {/* STEP 2: Segmentation 1 */}
                {step === 2 && <motion.div key="step2" custom={1} variants={variants} initial="enter" animate="center" exit="exit" transition={{
                duration: 0.4,
                ease: 'easeInOut'
              }} className="w-full" data-id="element-72">
                    <h3 className="text-2xl font-bold text-deep-blue mb-6" data-id="element-73">
                      ¿Qué es lo que más te interesa?
                    </h3>
                    <div className="space-y-4" data-id="element-74">
                      <button onClick={() => handleInterestSelect('Proteger a tu familia si algo te pasa mañana')} className="w-full text-left p-5 rounded-xl border-2 border-gray-100 bg-white hover:border-vitality-green hover:bg-green-50 transition-all duration-200 group" data-id="element-75">
                        <span className="block text-lg font-medium text-deep-blue group-hover:text-vitality-green" data-id="element-76">
                          Proteger a tu familia si algo te pasa mañana
                        </span>
                      </button>
                      <button onClick={() => handleInterestSelect('Construir un plan de ahorro seguro para tu jubilación o inversión')} className="w-full text-left p-5 rounded-xl border-2 border-gray-100 bg-white hover:border-vitality-green hover:bg-green-50 transition-all duration-200 group" data-id="element-77">
                        <span className="block text-lg font-medium text-deep-blue group-hover:text-vitality-green" data-id="element-78">
                          Construir un plan de ahorro seguro para tu jubilación
                          o inversión
                        </span>
                      </button>
                    </div>
                  </motion.div>}

                {/* STEP 3: Segmentation 2 */}
                {step === 3 && <motion.div key="step3" custom={1} variants={variants} initial="enter" animate="center" exit="exit" transition={{
                duration: 0.4,
                ease: 'easeInOut'
              }} className="w-full" data-id="element-79">
                    <h3 className="text-2xl font-bold text-deep-blue mb-6" data-id="element-80">
                      ¿Cuánto estarías dispuesto a invertir mensualmente?
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-81">
                      {['De $150 a $300', 'De $300 a $500', 'De $500 a $800', 'De $800 a $1,200'].map(option => <button key={option} onClick={() => handleInvestmentSelect(option)} className="p-5 rounded-xl border-2 border-gray-100 bg-white hover:border-vitality-green hover:bg-green-50 transition-all duration-200 group text-center" data-id="element-82">
                          <span className="block text-lg font-medium text-deep-blue group-hover:text-vitality-green" data-id="element-83">
                            {option}
                          </span>
                        </button>)}
                    </div>
                  </motion.div>}

                {/* STEP 4: Success */}
                {step === 4 && <motion.div key="step4" custom={1} variants={variants} initial="enter" animate="center" exit="exit" transition={{
                duration: 0.5,
                ease: 'easeOut'
              }} className="w-full h-full flex flex-col items-center justify-center text-center py-10" data-id="element-84">
                    <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  type: 'spring',
                  stiffness: 200,
                  damping: 20,
                  delay: 0.2
                }} data-id="element-85">
                      <CheckCircle2 className="w-24 h-24 text-vitality-green mb-6" data-id="element-86" />
                    </motion.div>
                    <h3 className="text-3xl font-bold text-deep-blue mb-4" data-id="element-87">
                      ¡Todo listo, {formData.name.split(' ')[0]}!
                    </h3>
                    <p className="text-lg text-gray-600 max-w-md mx-auto leading-relaxed mb-8" data-id="element-88">
                      Descarga tu guía ahora y obtén acceso a una consulta
                      personalizada para maximizar tus ahorros familiares.
                    </p>
                    <a href="https://gamma.app/docs/IUL-El-Plan-3-en-1-para-Proteger-a-tu-Familia-y-Jubilarte-con-Dig-hg4ru7rugrj9b1x" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-vitality-green hover:bg-vitality-green-hover text-white font-semibold py-4 px-8 rounded-xl text-lg transition-all duration-300 shadow-lg hover:shadow-xl" data-id="element-89">
                      <ArrowDownToLine className="w-5 h-5" data-id="element-90" />
                      Descargar mi guía gratuita
                    </a>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>;
}